import { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { HardHat, LogOut, Home, Plus, Settings, Sun, Moon, Wallet } from "lucide-react";
import { useTheme } from "@/hooks/useTheme";

export default function AppLayout({ children }: { children: ReactNode }) {
  const { signOut } = useAuth();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();

  const navItems = [
    { href: "/", icon: Home, label: "Panel" },
    { href: "/mvp/new", icon: Plus, label: "Nowa oferta" },
    { href: "/moje-stawki", icon: Wallet, label: "Moje stawki" },
    { href: "/ustawienia", icon: Settings, label: "Ceny" },
  ];

  return (
    <div
      className="min-h-screen"
      style={{ background: "var(--color-bg-page)", color: "var(--color-text-primary)" }}
    >
      {/* Top bar */}
      <header
        className="border-b"
        style={{ borderColor: "var(--color-border-default)", background: "var(--color-bg-surface)" }}
      >
        <div className="mx-auto flex max-w-[900px] items-center justify-between px-5 py-4">
          <Link to="/" className="flex items-center gap-2.5">
            <div
              className="flex h-8 w-8 items-center justify-center rounded-lg"
              style={{ background: "var(--color-accent-primary)", color: "#fff" }}
            >
              <HardHat className="h-5 w-5" />
            </div>
            <span className="hidden font-bold tracking-tight sm:block">Builder</span>
          </Link>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={toggleTheme}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border shadow-sm transition-opacity hover:opacity-95"
              style={{
                borderColor: theme === "dark" ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.14)",
                background: theme === "dark" ? "rgba(255,255,255,0.14)" : "rgba(0,0,0,0.06)",
                color: theme === "dark" ? "#fff" : "#111827",
              }}
              aria-label={theme === "dark" ? "Przełącz na jasny motyw" : "Przełącz na ciemny motyw"}
              title={theme === "dark" ? "Jasny motyw" : "Ciemny motyw"}
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <Link
              to="/ustawienia"
              className="flex items-center gap-2 text-sm transition-opacity hover:opacity-90"
              style={{ color: "var(--color-text-secondary)" }}
              aria-label="Ustawienia"
              title="Ustawienia"
            >
              <Settings className="h-4 w-4" />
            </Link>
            <button
              type="button"
              onClick={signOut}
              className="flex items-center gap-2 text-sm transition-opacity hover:opacity-90"
              style={{ color: "var(--color-text-secondary)" }}
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Wyloguj</span>
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="mx-auto max-w-[900px] px-5 py-10 pb-28 md:pb-12">
        {children}
      </main>

      {/* Mobile bottom nav */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 border-t md:hidden"
        style={{ borderColor: "var(--color-border-default)", background: "var(--color-bg-surface)" }}
      >
        <div className="flex justify-around py-2">
          {navItems.map((item) => {
            const isActive =
              item.href === location.pathname ||
              (item.href === "/mvp/new" && location.pathname.startsWith("/mvp"));
            return (
              <Link
                key={item.href}
                to={item.href}
                className={`flex flex-col items-center gap-1 px-4 py-2 text-xs font-medium transition-colors ${
                  isActive ? "" : ""
                }`}
                style={{
                  color: isActive ? "var(--color-accent-primary)" : "var(--color-text-secondary)",
                }}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
